/**
 * ParagonLibraryPanel — shared types and constants
 * Extracted from ParagonLibraryPanel.tsx (TASK-009)
 */

/* ── Core template type ── */
export interface ForgeTemplate {
  id: string
  name: string
  category: string
  thumbnail_url?: string | null
  config: Record<string, unknown>
}

/* ── Category → accent color map ── */
export const CATEGORY_COLORS: Record<string, { bg: string; text: string }> = {
  stylist:     { bg: 'rgba(244,114,182,0.15)', text: '#f472b6' },
  showcase:    { bg: 'rgba(167,139,250,0.15)', text: '#a78bfa' },
  interactive: { bg: 'rgba(96,165,250,0.15)',  text: '#60A5FA' },
  booking:     { bg: 'rgba(34,197,94,0.15)',   text: '#22c55e' },
  team:        { bg: 'rgba(245,158,11,0.15)',  text: '#f59e0b' },
  services:    { bg: 'rgba(251,113,133,0.15)', text: '#fb7185' },
  loyalty:     { bg: 'rgba(232,121,249,0.15)', text: '#e879f9' },
  social:      { bg: 'rgba(249,115,22,0.15)',  text: '#f97316' },
  info:        { bg: 'rgba(56,189,248,0.15)',  text: '#38bdf8' },
  products:    { bg: 'rgba(52,211,153,0.15)',  text: '#34d399' },
  timeline:    { bg: 'rgba(129,140,248,0.15)', text: '#818cf8' },
  contact:     { bg: 'rgba(232,121,249,0.15)', text: '#e879f9' },
  analytics:   { bg: 'rgba(163,230,53,0.15)',  text: '#a3e635' },
  hero:        { bg: 'rgba(34,211,238,0.15)',  text: '#22d3ee' },
  portfolio:   { bg: 'rgba(244,114,182,0.15)', text: '#f472b6' },
  commerce:    { bg: 'rgba(52,211,153,0.15)',  text: '#34d399' },
  about:       { bg: 'rgba(139,92,246,0.15)',  text: '#8b5cf6' },
  gallery:     { bg: 'rgba(236,72,153,0.15)',  text: '#ec4899' },
}

/* ── Forge category → CMS canvas block type ── */
export const FORGE_CATEGORY_TO_BLOCK: Record<string, string> = {
  stylist:     'team-block',
  showcase:    'gallery-block',
  interactive: 'cta-block',
  booking:     'booking-block',
  team:        'team-block',
  services:    'services-block',
  loyalty:     'cta-block',
  social:      'social-proof-block',
  info:        'hours-block',
  products:    'products-block',
  timeline:    'cta-block',
  contact:     'contact-block',
  analytics:   'cta-block',
  hero:        'hero-block',
  about:       'about-block',
  gallery:     'gallery-block',
}

/* ── Built-in demo templates ── */
export const DEMO_TEMPLATES: ForgeTemplate[] = [
  { id: 'forge-stylist-1',      name: 'Stylist Flip Card',       category: 'stylist',     config: { preset: 'default' } },
  { id: 'forge-testimonial-1',  name: 'Testimonial Carousel',    category: 'showcase',    config: { type: 'testimonial' } },
  { id: 'forge-gallery-1',      name: 'Gallery Lightbox',        category: 'showcase',    config: { type: 'gallery' } },
  { id: 'forge-beforeafter-1',  name: 'Before / After Slider',   category: 'interactive', config: { type: 'beforeafter' } },
  { id: 'forge-pricing-1',      name: 'Service Pricing Table',   category: 'interactive', config: { type: 'pricing' } },
  { id: 'forge-booking-1',      name: 'Booking Widget',          category: 'booking',     config: {} },
  { id: 'forge-team-1',          name: 'Team Roster Grid',        category: 'team',        config: {} },
  { id: 'forge-services-1',      name: 'Service Menu',            category: 'services',    config: {} },
  { id: 'forge-loyalty-1',       name: 'Loyalty Rewards Tracker', category: 'loyalty',     config: {} },
  { id: 'forge-social-1',        name: 'Social Proof Wall',       category: 'social',      config: {} },
  { id: 'forge-info-1',          name: 'Hours & Location',        category: 'info',        config: {} },
  { id: 'forge-products-1',      name: 'Product Showcase',        category: 'products',    config: {} },
  { id: 'forge-timeline-1',      name: 'Appointment Steps',       category: 'timeline',    config: {} },
  { id: 'forge-contact-1',       name: 'Contact & Inquiry Form',  category: 'contact',     config: {} },
  { id: 'forge-analytics-1',     name: 'Stats Counter',           category: 'analytics',   config: {} },
  { id: 'forge-hero-1',          name: 'Hero Banner',             category: 'hero',        config: {} },
  { id: 'forge-hero-2',          name: 'Hero Neon Parallax',      category: 'hero',        config: { preset: 'neon' } },
  { id: 'forge-about-1',         name: 'About Vision',            category: 'about',       config: { preset: 'vision' } },
  { id: 'forge-about-2',         name: 'About Approach Timeline', category: 'about',       config: { preset: 'approach' } },
  { id: 'forge-about-3',         name: 'Founder Story',           category: 'about',       config: { preset: 'founder' } },
  { id: 'forge-gallery-2',       name: 'Neon Masonry Gallery',    category: 'gallery',     config: { preset: 'neon-masonry' } },
  { id: 'forge-contact-2',       name: 'Neon Contact Section',    category: 'contact',     config: { preset: 'neon' } },
  { id: 'forge-team-2',          name: 'Team Garden Cards',       category: 'team',        config: { preset: 'garden' } },
  { id: 'forge-team-3',          name: 'Team High Contrast',      category: 'team',        config: { preset: 'high-contrast' } },
  { id: 'forge-team-4',          name: 'Team Matrix Cards',       category: 'team',        config: { preset: 'matrix' } },
]

/* ── Tier colors for badge styling ── */
export const TIER_COLORS: Record<string, string> = {
  'All':     'text-zinc-400 dark:text-white/30 bg-black/[0.03] dark:bg-white/[0.03] border-black/[0.08] dark:border-white/[0.06]',
  'Pro+':    'text-[rgba(99,102,241,0.7)] bg-[rgba(99,102,241,0.06)] border-[rgba(99,102,241,0.1)]',
  'Paragon': 'text-[#C9A84C] bg-[rgba(201,168,76,0.06)] border-[rgba(201,168,76,0.1)]',
}

/* ── Known categories for the "New Component" form ── */
export const KNOWN_CATEGORIES = [
  'stylist', 'showcase', 'interactive', 'booking', 'team',
  'services', 'loyalty', 'social', 'info', 'products',
  'timeline', 'contact', 'analytics', 'hero', 'portfolio', 'commerce',
  'about', 'gallery',
]

/* ── Collection labels for sidebar ── */
export const COLLECTIONS = ['Core', 'Booking', 'Portfolio', 'Commerce', 'Social', 'Paragon']
